import { useEffect, useState } from "react";
import { getLensHostStatusBus } from "./lensHostStatusBus.js";
/**
 * @typedef {import("./lensHostStatusBus.js").LensHostStatus} LensHostStatus
 * @typedef {import("./lensHostStatusBus.js").LensHostStatusState} LensHostStatusState
 */
/**
 * React hook that subscribes to the singleton lens-host status bus on
 * `window.__lensHostStatus`. Returns the current `{ status, fatalReason }`
 * and re-renders on every change.
 *
 * If no lens-host adapter is installed (or only `installLensHostPostMessage`,
 * which doesn't have a meaningful connection state), this returns
 * `{ status: "idle", fatalReason: null }` forever — harmless to keep
 * mounted regardless.
 *
 * Typical use is in the renderer's top-level component to render a
 * connection-error overlay:
 *
 * @example
 * function Renderer() {
 *   const { status, fatalReason } = useLensHostStatus();
 *   return (
 *     <>
 *       <LensCanvas ... />
 *       {fatalReason && <ConnectionError reason={fatalReason} />}
 *     </>
 *   );
 * }
 *
 * @returns {LensHostStatusState}
 */
export function useLensHostStatus() {
    const [state, setState] = useState(() => {
        const bus = getLensHostStatusBus();
        return bus?.state ?? { status: "idle", fatalReason: null };
    });
    useEffect(() => {
        const bus = getLensHostStatusBus();
        if (!bus)
            return undefined;
        // Sync once on mount in case the bus changed between render and effect.
        setState(bus.state);
        const onChange = () => setState(bus.state);
        bus.addEventListener("change", onChange);
        return () => bus.removeEventListener("change", onChange);
    }, []);
    return state;
}
//# sourceMappingURL=useLensHostStatus.js.map